import type { HowItWorksContent, HowItWorksStep } from '@/content/home';
import { CloudUpload } from '@/components/ui/Icons';

export type HowItWorksProps = {
  content: HowItWorksContent;
};

export function HowItWorks({ content }: HowItWorksProps) {
  return (
    <section
      id="how-it-works"
      aria-labelledby="how-heading"
      className="border-y border-rule bg-page px-6 py-[120px] md:px-[72px]"
    >
      <div className="mx-auto flex max-w-[1296px] flex-col items-center gap-20">
        <div className="flex w-full max-w-[760px] flex-col items-center gap-5 text-center">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-pencil">
            {content.eyebrow}
          </p>
          <h2
            id="how-heading"
            className="text-[32px] font-bold leading-[1.15] tracking-[-1px] text-ink md:text-[42px]"
          >
            {content.heading}
          </h2>
        </div>

        <div className="grid w-full grid-cols-1 gap-12 md:grid-cols-3 md:gap-6">
          {content.steps.map((step, idx) => (
            <Step key={step.number} step={step} isLast={idx === content.steps.length - 1} />
          ))}
        </div>
      </div>
    </section>
  );
}

function Step({ step, isLast }: { step: HowItWorksStep; isLast: boolean }) {
  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center">
        <span
          aria-hidden="true"
          className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-cloak-tint text-[16px] font-bold text-cloak-accent"
        >
          {step.number}
        </span>
        {!isLast && (
          <span aria-hidden="true" className="ml-6 hidden h-px flex-1 bg-cloak-accent/20 md:block" />
        )}
      </div>
      <h3 className="text-[22px] font-bold leading-[1.2] tracking-[-0.4px] text-ink md:text-[24px]">
        {step.title}
      </h3>
      <p className="text-[15px] leading-[1.6] text-graphite">{step.body}</p>
      <StepVisual visual={step.visual} />
    </div>
  );
}

function StepVisual({ visual }: { visual: HowItWorksStep['visual'] }) {
  if (visual === 'upload') {
    return (
      <div className="flex h-[120px] items-center justify-center gap-3.5 rounded-[10px] border border-rule bg-canvas p-6">
        <CloudUpload size={22} className="text-cloak-accent" />
        <span className="text-[13px] font-medium text-graphite">Drop a PDF or click to upload</span>
      </div>
    );
  }

  if (visual === 'review-screenshot') {
    return (
      <img
        src="/product-screenshots/003-cloakling.png"
        alt=""
        aria-hidden="true"
        className="h-[190px] w-full rounded-[10px] border border-rule object-cover"
      />
    );
  }

  // tools
  return (
    <div className="flex h-[120px] items-center justify-center gap-2 rounded-[10px] border border-rule bg-canvas px-4">
      {['ChatGPT', 'Claude', 'Internal IAG'].map((tool) => (
        <span
          key={tool}
          className="rounded-md border border-rule bg-page px-3 py-1.5 text-[12px] font-semibold text-graphite"
        >
          {tool}
        </span>
      ))}
    </div>
  );
}
